<template>
  <div class="keyword-tags-chart">
    <a ref="linker" class="keyword-tags-chart__linker" :href="clickedTagUrl" target="_blank">{{ clickedTagName }}</a>
    <div ref="chart" class="keyword-tags-chart__chart"></div>
  </div>
</template>

<script>
import * as d3 from 'd3'
// import { randArray } from '@/scripts/utils'

const TAGS_VALUE = [10, 8, 5, 2, 2, 1]
const TAGS_HIGHLIGHT_COLOR = ['#788cd7', '#9eade1']
const TAG_DEFAULT_COLOR = `#e6e6e6`

const TAGS_RADIUS = [40, 35, 32, 28, 28, 24]
const TAG_MAX_RADIUS = 50

const TAG_DEFAULT_STROKE = `#7a7a7a`

const SVG_CONFIG = {
  viewBox: '0 0 400 300',
  preserveAspectRatio: 'xMidYMid meet',
  width: 400,
  height: 300
}

export default {
  name: 'KeywordTagsChartD3',
  props: {
    keyword: { type: String, required: true },
    data: { type: Array, default: () => [] }
  },
  data () {
    return {
      chart: null,
      simulation: null,
      links: null,
      nodes: null,
      texts: null,
      clickedTagName: null,
      clickedTagUrl: null
    }
  },
  computed: {
    chartData () {
      let base = {
        id: 0,
        name: this.keyword,
        amount: Number(this.data[0].amount) * 1.5,
        value: TAGS_VALUE[0] * 1.5,
        radius: TAG_MAX_RADIUS,
        color: '#e4007f',
        fontColor: '#FFFFFF',
        links: []
      }
      let data = this.data.map((tag, index) => {
        let id = index + 1
        let value = TAGS_VALUE[index] || TAGS_VALUE[TAGS_VALUE.length - 1]
        let radius = TAGS_RADIUS[index] || TAGS_RADIUS[TAGS_RADIUS.length - 1]
        let color = tag.highlight < 0 ? TAG_DEFAULT_COLOR : TAGS_HIGHLIGHT_COLOR[tag.highlight]
        let fontColor = '#1a1a1a'
        let linkWith = [ 0 ]
        base.links.push({ source: 0, target: id })
        return {
          ...tag,
          id,
          value,
          radius,
          color,
          fontColor,
          linkWith,
          links: []
        }
      })
      // return [{ ...base, children: randArray(data) }]
      return [base].concat(data)
    }
  },
  methods: {
    onTagClick (data) {
      let tagName = data.name
      let urlPrefix = ''
      if (!tagName) {
        return
      }
      if (tagName !== this.keyword) {
        urlPrefix = 'https://udn.com/search/tagging/2'

        this.$ga('newmedia.send', {
          'hitType': 'event',
          'eventCategory': 'Read',
          'eventAction': 'Click',
          'eventLabel': 'Click_Tag'
        })

        console.log(`@${this.$options.name} [tagsClick]`)
      } else {
        urlPrefix = 'https://udn.com/search/result/2'

        this.$ga('newmedia.send', {
          'hitType': 'event',
          'eventCategory': 'Read',
          'eventAction': 'Click',
          'eventLabel': 'Click_UDNSearch'
        })

        console.log(`@${this.$options.name} [searchClick]`)
      }
      this.clickedTagName = tagName
      this.clickedTagUrl = `${urlPrefix}/${tagName}`
      this.$nextTick(() => {
        // this.$refs.linker.click()
        let bubble = true
        let cancelable = true
        let view = window
        this.$refs.linker.dispatchEvent(new MouseEvent('click', { bubble, cancelable, view }))
      })
    },
    onDragStarted (d) {
      if (!d3.event.active) {
        this.simulation.alphaTarget(0.3).restart()
      }
      d.fx = d.x
      d.fy = d.y
    },
    onDragged (d) {
      d.fx = d3.event.x
      d.fy = d3.event.y
    },
    onDragEnded (d) {
      if (!d3.event.active) {
        this.simulation.alphaTarget(0)
      }
      d.fx = null
      d.fy = null
    },
    onTicked () {
      this.links
        .attr('x1', (d) => d.source.x)
        .attr('y1', (d) => d.source.y)
        .attr('x2', (d) => d.target.x)
        .attr('y2', (d) => d.target.y)

      this.nodes
        .attr('cx', (d) => d.x)
        .attr('cy', (d) => d.y)

      this.texts
        .attr('x', (d) => d.x)
        .attr('y', (d) => d.y)
    },
    drawChart () {
      let data = this.chartData.slice(0)
      let linksData = data[0].links

      let svg = d3.select(this.$refs.chart)
        .append('svg')
        .attr('viewBox', SVG_CONFIG.viewBox)
        .attr('preserveAspectRatio', SVG_CONFIG.preserveAspectRatio)
        .attr('width', SVG_CONFIG.width)
        .attr('height', SVG_CONFIG.height)

      let simulation = d3.forceSimulation()
        .force('link', d3.forceLink())
        .force('charge', d3.forceManyBody())
        .force('center', d3.forceCenter(SVG_CONFIG.width / 2, SVG_CONFIG.height / 2))
        .force('collision', d3.forceCollide().radius((d) => d.radius * 1.1))

      let links = svg.append('g')
        .selectAll('line')
        .data(linksData)
        .enter()
        .append('line')
        .classed('tag-link', true)
        .attr('stroke-width', 0.75)
        .attr('stroke', TAG_DEFAULT_STROKE)
        .attr('stroke-opacity', 0.75)

      let nodes = svg.append('g')
        .selectAll('circle')
        .data(data)
        .enter()
        .append('circle')
        .classed('tag', true)
        .attr('r', (d) => d.radius)
        .attr('fill', (d) => d.color)
        .attr('stroke', 'white')
        .attr('stroke-width', 1)
        .on('click', this.onTagClick)
        .call(d3.drag()
          .on('start', this.onDragStarted)
          .on('drag', this.onDragged)
          .on('end', this.onDragEnded))

      let texts = svg.selectAll('text')
        .data(data)
        .enter()
        .append('text')
        .classed('tag-name', true)
        .classed('tag-keyword', (d) => d.name === this.keyword)
        .attr('dy', '0.3em')
        .attr('dominant-baselint', 'middle')
        .style('fill', (d) => d.fontColor)
        .text((d) => d.name)

      simulation
        .nodes(this.chartData)
        .on('tick', this.onTicked)

      simulation.force('link')
        .links(linksData)
        // .distance(50)
        .distance(100)

      simulation.force('charge')
        // .strength(-20)
        .strength(-15)

      this.simulation = simulation
      this.links = links
      this.nodes = nodes
      this.texts = texts
    }
  },
  mounted () {
    this.drawChart()
  }
}
</script>

<style lang="scss">
.keyword-tags-chart {
  position: relative;
  width: 100%;
  height: 100%;
  font: normal 100%/150% "Montserrat", "Noto Sans TC", sans-serif;

  &__linker {
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    cursor: pointer;
  }

  &__chart {
    position: absolute;
    width: 100%;
    height: 100%;
    min-width: 400px;
    left: 50%;
    transform: translateX(-50%);

    svg {
      position: absolute;
      width: 100%;
      height: 100%;
    }

    .tag {
      cursor: pointer;
    }

    .tag-link {
    }

    .tag-name {
      font-size: 1em;
      text-anchor: middle;
      user-select: none;
      pointer-events: none;
      @include rwd($RWD_TABLET_SMALL) {
        font-size: 0.75em;
      }
      @include rwd($RWD_TABLET) {
        font-size: 0.65em;
      }
      @include rwd($RWD_DESKTOP) {
        font-size: 0.5em;
      }
      @include rwd($RWD_DESKTOP_WIDE) {}
    }

    .tag-keyword {
      font-size: 1.15em;
      text-anchor: middle;
      user-select: none;
      pointer-events: none;
      @include rwd($RWD_TABLET_SMALL) {
        font-size: 1em;
      }
      @include rwd($RWD_TABLET) {
        font-size: 0.8em;
      }
      @include rwd($RWD_DESKTOP) {
        font-size: 0.65em;
      }
      @include rwd($RWD_DESKTOP_WIDE) {
        font-size: 0.5em;
      }
    }
  }
}
</style>
